import React from 'react';
import { observer } from 'mobx-react-lite';

import styled from '@emotion/styled';
import { css } from 'emotion';

import Container from '@vromo/ui/components/Container';

import isValidLatLon from '@vromo/utils/isValidLatLon';

import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import last from 'lodash/last';
import entries from 'lodash/entries';

import startOfDay from 'date-fns/start_of_day';

import JobsCancelledChart from './components/JobsCancelledChart';
import CancelReasonChart from './components/CancelReasonChart';
import JobsDistributionMap from './components/JobsDistributionMap';
import QuickStatsTable from './components/QuickStatsTable';
import DayOfWeekJobChart from './components/DayOfWeekJobChart';
import JobsCompletedAreaChart from './components/JobsCompletedAreaChart';
import HourlyBarChart from './components/HourlyBarChart';
import { Job } from 'models';

const SegmentRow = styled.div`
    display: flex;
    flex-direction: row;

    @media (max-width: 1000px) {
        flex-direction: column;
    }
`;

const Segment = css`
    padding: 20px;
    flex: 1;
    height: 300px;

    @media (max-width: 1000px) {
        flex: unset;
    }
`;

interface Props {
    startDate: Date;
    endDate: Date;
    jobsCompleted: Job[];
    jobsCancelled: Job[];
}

export default observer<Props>(function JobReport({
    startDate,
    endDate,
    jobsCompleted,
    jobsCancelled
}) {
    const jobsWithRatings = () => {
        return jobsCompleted.filter((job) => {
            const items = job.ratings!.items;
            return items && items.length > 0;
        });
    };

    const averageJobRating = () => {
        const filteredJobs = jobsWithRatings();

        return (
            (filteredJobs.reduce((sum, job) => {
                const items = job.ratings!.items;
                return sum + last(items)!.ratio;
            }, 0) /
                filteredJobs.length) *
            5
        );
    };

    const jobLocations = () => {
        return flatMap(jobsCompleted, (job) =>
            job.tasks.items
                .filter(({ zone }) => zone && isValidLatLon(zone.coords.lat, zone.coords.lon))
                .map(({ zone }) => [zone!.coords.lon, zone!.coords.lat])
        ) as [number, number][];
    };

    const jobsByDay = () => {
        return entries(
            groupBy(jobsCompleted, (job) => +startOfDay(last(job.dispatches!.items)!.created || ''))
        ).map((pair) => ({ key: +pair[0], value: pair[1].length }));
    };

    const jobsByDayOfWeek = () => {
        return entries(
            groupBy(jobsCompleted, (job) =>
                new Date(last(job.dispatches!.items)!.created || '').getDay()
            )
        ).map((pair) => ({ key: +pair[0], value: pair[1].length }));
    };

    const jobsBy24Hour = () => {
        return entries(
            groupBy(jobsCompleted, (job) =>
                new Date(last(job.dispatches!.items)!.created || '')?.getHours()
            )
        )
            .map((pair) => ({ key: +pair[0], value: pair[1].length }))
            .sort(function (a, b) {
                return a.key - b.key;
            });
    };

    const jobsBy12Hour = () => {
        return jobsBy24Hour().map((job) => {
            const ampm = job.key >= 12 ? 'pm' : 'am';
            let temp = job.key % 12;
            temp = temp !== 0 ? temp : 12;

            return { key: `${temp}${ampm}`, value: job.value };
        });
    };

    return (
        <div style={{ flex: 1 }}>
            {/* row 1 */}
            <SegmentRow>
                {/* quick stats table */}
                <Container className={Segment} title="Quick Statistics">
                    <QuickStatsTable
                        actualJobsCancelled={jobsCancelled.length}
                        actualJobsCompleted={jobsCompleted.length}
                        averageJobRating={averageJobRating()}
                    />
                </Container>

                {/* heatmap */}
                <Container className={Segment} title="Location Distribution of Jobs">
                    <JobsDistributionMap points={jobLocations()} />
                </Container>

                {/* Day distribution of jobs */}
                <Container className={Segment} title="Day Distribution of Jobs">
                    <DayOfWeekJobChart jobsByDayOfWeek={jobsByDayOfWeek()} />
                </Container>
            </SegmentRow>

            {/* row 2 */}
            <SegmentRow>
                {/* jobs over time */}
                <Container className={Segment} title="Jobs Completed">
                    <JobsCompletedAreaChart
                        startDate={startDate}
                        endDate={endDate}
                        jobsByDay={jobsByDay()}
                    />
                </Container>

                {/* jobs hourly */}
                <Container className={Segment} title="Hourly Distribution of Jobs">
                    <HourlyBarChart jobsByHourCompleted={jobsBy12Hour()} />
                </Container>
            </SegmentRow>
            <SegmentRow>
                <Container className={Segment} title="Jobs Cancelled">
                    <JobsCancelledChart
                        jobs={jobsCancelled}
                        startDate={startDate}
                        endDate={endDate}
                    />
                </Container>
                <Container className={Segment} title="Job Cancellation Reason">
                    <CancelReasonChart cancelledJobs={jobsCancelled} />
                </Container>
            </SegmentRow>
        </div>
    );
});
